/* eslint-disable @typescript-eslint/no-explicit-any */
import { RpcError } from "grpc-web";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// Hooks
import useClaimsToast from "../hooks/useToast";

// Protos
import { Case } from "../utils/api/protos/messages/cases.message_pb";
import {
	Activity,
	ActivityType,
	ChecklistType,
	ClaimPerson,
	ClaimsDueDates,
	ClaimsStatus,
	NoteTypeName,
	UpsertClaimsStatusReasonsRequest
} from "../utils/api/protos/messages/claims.message_pb";

// Types
import { Claim, ClaimService, LegalBenefit, NotesActivitiesDisplay, User } from "../utils/types";

// Utils
import { getCase } from "../utils/api/cases";
import {
	assignAnalyst,
	getActivityList,
	getActivityTypesList,
	getAnalystList,
	getClaim,
	getNotesByClaim,
	createNote,
	postApproveClaim,
	postCancelClaim,
	postDenyClaim,
	postDuplicateClaim,
	postUncancelClaim,
	upsertClaim,
	postQueueClaimForPayment,
	postClosePaidClaim,
	postCloseNotPaidClaim,
	postCloseNotPaidGoodwillClaim,
	upsertChecklistItem,
	postPendingProviderClaim,
	postPendingVerificationClaim,
	postPendingInternalReviewClaim,
	postInReviewClaim,
	getIsPremiumPaid,
	postUpdateClaimsDueDates,
	getPinnedNotesByClaim,
	pinNote,
	unpinNote,
	postUpdateClaimCleanDate,
	postClaimsStatusReasons
} from "../utils/api/claims";
import { generateCcsPdf } from "../utils/api/pdf";
import {
	convertBffClaimToLocalClaim,
	convertBffLegalBenefitToLocalLegalBenefit,
	dateToInt64UTC
} from "../utils/helpers";
import { getLegalBenefitById } from "../utils/api/benefits";
import {
	getServiceLabelFn,
	handleError,
	updateBenefitsUtil,
	handleFetchCaseUtil,
	handleClaimUpsertUtil
} from "../utils/provider/claims-provider-util";
import { useAuth } from "../hooks/useAuth";
import { ClaimServiceProvidedFor, UserRole } from "../utils/consts";
import usePermission from "../hooks/usePermission";

export type ClaimDetailsContextType = {
	claim: Claim | undefined;
	claimCase: Case | undefined;
	benefits: LegalBenefit[];
	analysts: ClaimPerson[] | undefined;
	activities: Activity[] | undefined;
	notes: NotesActivitiesDisplay[];
	pinnedNotes: NotesActivitiesDisplay[];
	activityTypes: ActivityType[] | undefined;
	isLoading: boolean;
	hasMoreActivities: boolean;
	hasMoreNotes: boolean;
	isPremiumPaid: boolean | undefined;
	handleFetchClaim: (claimId: number) => Promise<void>;
	handleApproveClaim: () => Promise<void>;
	handleDenyClaim: (denyReason: string) => Promise<void>;
	handleInReview: () => Promise<void>;
	handleCancelClaim: (cancelReason: string) => Promise<void>;
	handleUncancelClaim: () => Promise<void>;
	handleQueueClaimForPayment: () => Promise<void>;
	handleClosePaidClaim: () => Promise<void>;
	handleCloseNotPaidClaim: () => Promise<void>;
	handleCloseNotPaidGoodwillClaim: () => Promise<void>;
	handleDuplicateClaim: () => Promise<void>;
	handlePendingProvider: (reasons: (string | number)[], note: string | undefined, sendEmail: boolean) => Promise<void>;
	handlePendingVerification: () => Promise<void>;
	handlePendingInternalReview: () => Promise<void>;
	handleClaimUpsert: (updatedClaim: Claim) => Promise<void>;
	handleHoldForAuditChange: (isHeldForAudit: boolean) => Promise<void>;
	handleFetchActivities: (page?: number, filterRole?: string, filterActivityType?: string) => Promise<void>;
	handleLoadMoreActivities: (filterRole?: string, filterActivityType?: string) => void;
	handleAssignAnalyst: (analystId: number) => Promise<void>;
	handleUpsertChecklistItem: (
		id: number | undefined,
		label: string,
		checklistType: ChecklistType,
		value: boolean
	) => Promise<void>;
	handleFetchNotes: (claimId: number, filterRole?: string, filterNoteType?: string) => Promise<void>;
	handleFetchPinnedNotes: (claimId: number) => Promise<void>;
	handleCreateNote: (claimId: number, note: string, noteTypeName: NoteTypeName) => Promise<void>;
	handleLoadMoreNotes: (filterRole?: string, filterNoteType?: string) => void;
	handlePinNote: (noteId: number) => Promise<void>;
	handleUnpinNote: (noteId: number) => Promise<void>;
	handleIsPremiumPaid: () => Promise<void>;
	getServiceLabel: (claimService: any) => string;
	handleUpdateClaimDueDate: (newDate: Date) => Promise<void>;
	handleUpdateClaimCleanDate: (newDate: Date) => Promise<void>;
	handleGeneratePdf: (openPdfInNewTab?: boolean) => Promise<void>;
};

interface ClaimDetailsProviderProps {
	children: React.ReactNode;
}

const ClaimDetailsContext = createContext<ClaimDetailsContextType | undefined>(undefined);

export const ClaimDetailsProvider: React.FC<ClaimDetailsProviderProps> = ({ children }) => {
	const [claim, setClaim] = useState<Claim | undefined>();
	const [benefits, setBenefits] = useState<LegalBenefit[]>([]);
	const [claimCase, setCase] = useState<Case | undefined>();
	const [analysts, setAnalysts] = useState<ClaimPerson[] | undefined>();
	const [activities, setActivities] = useState<Activity[]>([]);
	const [notes, setNotes] = useState<NotesActivitiesDisplay[]>([]);
	const [pinnedNotes, setPinnedNotes] = useState<NotesActivitiesDisplay[]>([]);
	const [activityTypes, setActivityTypes] = useState<ActivityType[] | undefined>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [hasMoreActivities, setHasMoreActivities] = useState<boolean>(true);
	const [currentActivityPage, setCurrentActivityPage] = useState<number>(1);
	const [hasMoreNotes, setHasMoreNotes] = useState<boolean>(true);
	const [currentNotePage, setCurrentNotePage] = useState<number>(1);
	const [isPremiumPaid, setIsPremiumPaid] = useState<boolean | undefined>(undefined);
	const ACTIVITIES_PER_PAGE = 10;
	const NOTES_PER_PAGE = 10;
	const toast = useClaimsToast();
	const { getUser } = useAuth();
	const user = getUser();
	const userRoles = (user?.role?.split(",") as UserRole[]) ?? [];
	const hasAdminAccess =
		userRoles.includes(UserRole.ClaimsAdmin) ||
		userRoles.includes(UserRole.ClaimsAnalyst) ||
		userRoles.includes(UserRole.ClaimsManager) ||
		userRoles.includes(UserRole.ClaimsViewOnly) ||
		userRoles.includes(UserRole.ClaimsNetworkManager) ||
		userRoles.includes(UserRole.Payments);
	const { hasGeneralManagerAccess, hasGeneralAdminAccess } = usePermission();

	// On mount, fetch initial data (analysts and activity types)
	useEffect(() => {
		if (!hasAdminAccess) return;
		if (!claim?.assignedAnalyst) return;

		if (hasGeneralManagerAccess) {
			handleFetchAnalysts();
		} else if (hasGeneralAdminAccess) {
			handleFetchAnalysts(UserRole.ClaimsManager).then(() => {
				//Add assigned analyst to list of analysts when fetching only managers.
				setAnalysts((prevAnalysts) => {
					const assignedAnalyst = claim?.assignedAnalyst;
					if (assignedAnalyst) {
						return [...(prevAnalysts || []), assignedAnalyst];
					}
					return prevAnalysts;
				});
			});
		} else if (claim?.assignedAnalyst) {
			// For unpriveledged roles, don't fetch analysts, just set the assigned analyst
			setAnalysts([claim?.assignedAnalyst]);
		}
		handleFetchActivityTypes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claim?.assignedAnalyst]);

	// Use shared updateBenefitsUtil to load legal benefits when the claim changes
	useEffect(() => {
		async function updateBenefitsInternal() {
			if (claim && claim.claimId && claim.claimServices.length > 0) {
				const updatedBenefits = await updateBenefitsUtil(claim, benefits, async (legalBenefitId: number) => {
					try {
						const result = await getLegalBenefitById(legalBenefitId);
						const bffBenefit = result.getBenefit();
						if (bffBenefit) {
							return convertBffLegalBenefitToLocalLegalBenefit(bffBenefit);
						}
					} catch (err) {
						console.error(`Error fetching benefit ${legalBenefitId}`, err);
						toast("Error", "Failed to load benefit data", "error");
					}
					return undefined;
				});
				setBenefits(updatedBenefits);
			}
		}
		updateBenefitsInternal();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claim]);

	// Fetch activities with pagination
	const handleFetchActivities = useCallback(
		async (page?: number, filterRole?: string, filterActivityType?: string) => {
			try {
				if (!claim?.claimId) return;

				const currentPage = page || currentActivityPage;
				const result = await getActivityList(
					claim.claimId,
					currentPage,
					ACTIVITIES_PER_PAGE,
					filterRole,
					filterActivityType
				);
				const activityList = result.getActivitiesList();
				const paginationResponse = result.getPaginationResponse();

				if (activityList) {
					// Sort activities by created_at in descending order (newest first)
					const sortedActivities = [...activityList].sort((a, b) => b.getCreatedAt() - a.getCreatedAt());

					// Update activities list
					if (currentPage === 1) {
						setActivities(sortedActivities);
					} else {
						setActivities((prev) => [...prev, ...sortedActivities]);
					}

					// Check if there are more pages
					let hasMore = false;
					if (paginationResponse) {
						const totalItems = paginationResponse.getTotal();
						const totalPages = Math.ceil(totalItems / ACTIVITIES_PER_PAGE);
						hasMore = currentPage < totalPages;
					} else {
						hasMore = sortedActivities.length === ACTIVITIES_PER_PAGE;
					}

					setHasMoreActivities(hasMore);
					if (hasMore) {
						setCurrentActivityPage(currentPage + 1);
					}
				}
			} catch (error) {
				handleError(toast, error as RpcError, "There was a problem fetching activities", "Error fetching activities");
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[claim?.claimId, currentActivityPage]
	);

	// Reset activities when claim changes
	useEffect(() => {
		if (claim?.claimId) {
			const currentClaimId = activities[0]?.getClaimId();
			if (hasAdminAccess && (!activities.length || currentClaimId !== claim.claimId)) {
				setActivities([]);
				setCurrentActivityPage(1);
				setHasMoreActivities(true);
				handleFetchActivities(1);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claim?.claimId]);

	// Shared getServiceLabel using the utility
	const getServiceLabel = (claimService: any): string => {
		if (!claimService?.serviceId) return "";
		return getServiceLabelFn(benefits, claimService);
	};

	//This was extracted into an internal and external function
	//The internal function was created because we have two use cases:
	// 1. When we want to fetch claim from external components (set loading)
	// 2. When we want to fetch claim internally after an action (no loading state change)
	const _handleFetchClaim = async (claimId: number): Promise<void> => {
		try {
			const result = await getClaim(claimId);
			const responseClaim = result.getClaim();
			if (responseClaim) {
				setClaim(convertBffClaimToLocalClaim(responseClaim));
				// Fetch the claim's case if not already
				if (claimCase == null) {
					await handleFetchCase(responseClaim.getCaseId());
				}
			}
		} catch (error) {
			handleError(toast, error as RpcError, "There was a problem fetching the Claim", "Error fetching claim");
		}
	};

	//For use by importing components
	const handleFetchClaim = async (claimId: number) => {
		setIsLoading(true);
		await _handleFetchClaim(claimId);
		setIsLoading(false);
	};

	// Shared handleFetchCase using the utility
	const handleFetchCase = async (caseId: number): Promise<void> => {
		setIsLoading(true);
		try {
			const fetchedCase = await handleFetchCaseUtil(caseId, getCase);
			if (fetchedCase) {
				setCase(fetchedCase);
			} else {
				toast("Error", "Unable to fetch Case associated to this Claim", "error");
			}
		} catch (error) {
			console.error(error);
			toast("Error", (error as RpcError).message, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const canSubmitStatusChange = (): boolean => {
		if (claim?.isHeldForAudit) {
			toast("Error", "Status changes are disabled while this claim is held for audit", "error");
			return false;
		}
		return true;
	};

	// Shared handleClaimUpsert using the utility
	const handleClaimUpsert = async (updatedClaim: Claim) => {
		setIsLoading(true);
		try {
			const submittedClaim = await handleClaimUpsertUtil(updatedClaim, upsertClaim);
			if (submittedClaim) {
				setClaim(submittedClaim);
			}
		} catch (error: any) {
			console.error(error);
			toast("Error", error, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleHoldForAuditChange = async (isHeldForAudit: boolean): Promise<void> => {
		if (!claim?.claimId) {
			toast("Error", "Unable to update audit hold for this claim", "error");
			return;
		}

		setIsLoading(true);
		try {
			const submittedClaim = await handleClaimUpsertUtil({ ...claim, isHeldForAudit }, upsertClaim);
			if (submittedClaim) {
				setClaim(submittedClaim);
			}
			await _handleFetchClaim(claim.claimId);
			await handleFetchActivities(1);
			toast("Success", isHeldForAudit ? "Claim held for audit" : "Claim released from audit hold", "success");
		} catch (error: any) {
			console.error(error);
			toast("Error", error?.message || error || "Unable to update audit hold", "error");
		} finally {
			setIsLoading(false);
		}
	};

	// The remainder of the functions (assigning analysts, notes, etc.) remain as in your original provider.
	// For brevity, they are kept as-is below.

	const handleLoadMoreActivities = useCallback(
		async (filterRole?: string, filterActivityType?: string) => {
			if (!hasMoreActivities) return;
			await handleFetchActivities(undefined, filterRole, filterActivityType);
		},
		[hasMoreActivities, handleFetchActivities]
	);

	const handleFetchAnalysts = async (filterRole?: string) => {
		setIsLoading(true);
		try {
			const result = await getAnalystList(filterRole);
			const analystList = result.getAnalystsList();
			if (analystList) {
				setAnalysts(analystList.sort((a, b) => (a.getName() < b.getName() ? -1 : 1)));
			}
		} catch (error) {
			handleError(toast, error as RpcError, "There was a problem fetching analysts", "Error fetching analysts");
		} finally {
			setIsLoading(false);
		}
	};

	const handleFetchNotes = useCallback(
		async (claimId: any, filterRole?: string, filterNoteType?: string) => {
			try {
				if (!claimId) {
					console.error("No claim ID provided to handleFetchNotes");
					return;
				}
				const response = await getNotesByClaim(claimId, 1, NOTES_PER_PAGE, filterRole, filterNoteType);
				const notesList = response.getNotesList();
				const paginationResponse = response.getPaginationResponse();

				const notesArray = notesList
					.filter((note) => !note.getIsPinned()) // Exclude pinned notes - they're fetched separately
					.map((note) => {
						const noteType = note.getNoteType();
						return {
							id: note.getId(),
							claimId: note.getClaimId(),
							userId: note.getUserId(),
							userName: note.getUserName(),
							userRole: note.getUserRole(),
							type: {
								noteType: noteType?.getName(),
								label: noteType?.getLabel(),
								color: noteType?.getColor()
							},
							message: note.getNote(),
							isPinned: note.getIsPinned(),
							createdAt: note.getCreatedAt()
						};
					});

				setNotes(notesArray);

				if (paginationResponse) {
					const totalItems = paginationResponse.getTotal();
					setHasMoreNotes(totalItems > NOTES_PER_PAGE);
					setCurrentNotePage(2);
				} else {
					setHasMoreNotes(notesArray.length === NOTES_PER_PAGE);
					setCurrentNotePage(2);
				}
			} catch (error) {
				handleError(toast, error as RpcError, "Unable to load notes. Please try again later.", "Error fetching notes");
				setNotes([]);
				setHasMoreNotes(false);
			}
		},
		[toast]
	);

	const handleFetchPinnedNotes = useCallback(
		async (claimId: any) => {
			try {
				if (!claimId) {
					console.error("No claim ID provided to handleFetchPinnedNotes");
					return;
				}
				const response = await getPinnedNotesByClaim(claimId);
				const notesList = response.getNotesList();

				const notesArray = notesList.map((note) => {
					const noteType = note.getNoteType();
					return {
						id: note.getId(),
						claimId: note.getClaimId(),
						userId: note.getUserId(),
						userName: note.getUserName(),
						userRole: note.getUserRole(),
						type: {
							noteType: noteType?.getName(),
							label: noteType?.getLabel(),
							color: noteType?.getColor()
						},
						message: note.getNote(),
						isPinned: note.getIsPinned(),
						createdAt: note.getCreatedAt()
					};
				});

				setPinnedNotes(notesArray);
			} catch (error) {
				handleError(
					toast,
					error as RpcError,
					"Unable to load pinned notes. Please try again later.",
					"Error fetching pinned notes"
				);
				setPinnedNotes([]);
			}
		},
		[toast]
	);

	const handleCreateNote = async (claimId: any, note: string, noteTypeName: NoteTypeName) => {
		try {
			const response = await createNote(claimId, note, noteTypeName);
			const newNote = response.getNote();
			if (newNote) {
				const newNoteDisplay = {
					id: newNote.getId(),
					claimId: newNote.getClaimId(),
					userId: newNote.getUserId(),
					userName: newNote.getUserName(),
					userRole: newNote.getUserRole(),
					type: {
						noteType: newNote.getNoteType()?.getName(),
						label: newNote.getNoteType()?.getLabel(),
						color: newNote.getNoteType()?.getColor()
					},
					message: newNote.getNote(),
					createdAt: newNote.getCreatedAt()
				};

				setNotes([newNoteDisplay, ...notes]);
			}
		} catch (error) {
			console.error("Error creating note:", error);
		}
	};

	const handleFetchActivityTypes = async () => {
		try {
			const result = await getActivityTypesList();
			const activityTypesList = result.getActivityTypesList();
			if (activityTypesList) {
				setActivityTypes(activityTypesList);
			}
		} catch (error) {
			handleError(
				toast,
				error as RpcError,
				"There was a problem fetching activity types",
				"Error fetching activity types"
			);
		}
	};

	const handleApproveClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			const currentUser: User | undefined = getUser();
			if (claim && claim.claimId && currentUser) {
				const isMissingRecipientAge: boolean = claim.claimServices.some((svc: ClaimService) => {
					return svc.serviceProvidedFor === ClaimServiceProvidedFor.DependentChild && !svc.recipientAge;
				});
				if (!claim.checklistComplete) {
					const errorMessage = "Checklist is not complete.";
					console.error(errorMessage);
					toast("Error", errorMessage, "error");
					throw new Error(errorMessage);
				} else if (isMissingRecipientAge) {
					const errorMessage: string = "Missing Recipient Age.";
					console.error(errorMessage);
					toast("Error", errorMessage, "error");
					throw new Error(errorMessage);
				} else {
					await postApproveClaim(claim.claimId);
					await _handleFetchClaim(claim.claimId);
					await handleFetchActivities();
					toast("Success", "Successfully Approved Claim", "success");
				}
			} else {
				console.error("Attempting to Approve a Claim that doesn't exist!");
				toast("Error", "Unable to Approve Claim, please contact Support", "error");
			}
		} catch (err: any) {
			if (err.message) {
				console.error("There was a problem Approving the Claim: " + err.message, err);
				toast("Error", "There was a problem approving the Claim: " + err.message, "error");
			} else {
				console.error("There was a problem Approving the Claim", err);
				toast("Error", "There was a problem approving the Claim", "error");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleDenyClaim = async (denyReason: string): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postDenyClaim(claim.claimId, denyReason);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Denied Claim", "success");
			} else {
				console.error("Attempting to Deny a Claim that doesn't exist!");
				toast("Error", "Unable to Deny Claim, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Denying the Claim\n", err);
			toast("Error", "There was a problem Denying the Claim", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleInReview = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postInReviewClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully changed status to In Review", "success");
			} else {
				console.error("Attempting to change status to In Review for a Claim that doesn't exist!");
				toast("Error", "Unable to change status to In Review, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem changing status to In Review\n", err);
			toast("Error", "There was a problem changing status to In Review", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancelClaim = async (cancelReason: string): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postCancelClaim(claim.claimId, cancelReason);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Cancelled Claim", "success");
			} else {
				console.error("Attempting to Cancel a Claim that doesn't exist!");
				toast("Error", "Unable to Cancel Claim, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Cancelling the Claim\n", err);
			toast("Error", "There was a problem Cancelling the Claim", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUncancelClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postUncancelClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Uncancelled Claim", "success");
			} else {
				console.error("Attempting to Uncancel a Claim that doesn't exist!");
				toast("Error", "Unable to Uncancel Claim, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Uncanceling the Claim\n", err);
			toast("Error", "There was a problem Uncanceling the Claim", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleQueueClaimForPayment = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postQueueClaimForPayment(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Queued Claim For Payment", "success");
			} else {
				console.error("Attempting to Queue For Payment a Claim that doesn't exist!");
				toast("Error", "Unable to Queue Claim For Payment, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Queueing the Claim For Payment\n", err);
			toast("Error", "There was a problem Queueing the Claim For Payment", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleClosePaidClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postClosePaidClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Closed Paid Claim", "success");
			} else {
				console.error("Attempting to Close Paid Claim that doesn't exist!");
				toast("Error", "Unable to Close Paid Claim, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Closing Paid Claim\n", err);
			toast("Error", "There was a problem Closing Paid Claim", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseNotPaidClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postCloseNotPaidClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully Closed Not-Paid Claim", "success");
			} else {
				console.error("Attempting to Close Not-Paid Claim that doesn't exist!");
				toast("Error", "Unable to Close Not-Paid Claim, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem Closing Not-Paid Claim\n", err);
			toast("Error", "There was a problem Closing Not-Paid Claim", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseNotPaidGoodwillClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postCloseNotPaidGoodwillClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully marked claim as Goodwill Payment", "success");
			} else {
				console.error("Attempting to mark Goodwill Payment on a claim that doesn't exist!");
				toast("Error", "Unable to mark as Goodwill Payment, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem marking claim as Goodwill Payment\n", err);
			toast("Error", "There was a problem marking claim as Goodwill Payment", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleDuplicateClaim = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postDuplicateClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully marked claim as Duplicate", "success");
			} else {
				console.error("Attempting to mark Duplicate on a claim that doesn't exist!");
				toast("Error", "Unable to mark as Duplicate, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem marking claim as Duplicate\n", err);
			toast("Error", "There was a problem marking claim as Duplicate", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handlePendingProvider = async (
		reasons: (string | number)[],
		note: string | undefined,
		sendEmail: boolean
	): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				// First post the status reasons, then update the status
				await postClaimsStatusReasons(claim.claimId, ClaimsStatus.PENDING_PROVIDER, reasons, note).then(async () => {
					await postPendingProviderClaim(claim.claimId as number, sendEmail);
					await _handleFetchClaim(claim.claimId as number);
					await handleFetchActivities();
				});
				toast("Success", "Successfully changed status to Pending Provider", "success");
			} else {
				console.error("Attempting to change status to Pending Provider for a Claim that doesn't exist!");
				toast("Error", "Unable to change status to Pending Provider, please contact Support", "error");
			}
		} catch (err: any) {
			switch (err.errorCode) {
				case "attorney_not_active":
					// If the error is that the attorney is not active, the update worked but just didn't send out the email. So refresh the claim and activities
					await _handleFetchClaim(claim?.claimId as number);
					await handleFetchActivities();
					toast("Success", "Successfully changed status to Pending Provider", "success");
					toast("Warning", "Attorney is not active, so an email will need to be manually sent", "warning");
					break;
				default:
					// eslint-disable-next-line no-throw-literal
					console.error("There was a problem changing status to Pending Provider\n", err);
					toast("Error", "There was a problem changing status to Pending Provider", "error");
					break;
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handlePendingVerification = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postPendingVerificationClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully changed status to Pending Verification", "success");
			} else {
				console.error("Attempting to change status to Pending Verification for a Claim that doesn't exist!");
				toast("Error", "Unable to change status to Pending Verification, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem changing status to Pending Verification\n", err);
			toast("Error", "There was a problem changing status to Pending Verification", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handlePendingInternalReview = async (): Promise<void> => {
		setIsLoading(true);
		if (!canSubmitStatusChange()) {
			setIsLoading(false);
			return;
		}
		try {
			if (claim && claim.claimId) {
				await postPendingInternalReviewClaim(claim.claimId);
				await _handleFetchClaim(claim.claimId);
				await handleFetchActivities();
				toast("Success", "Successfully changed status to Pending Internal Review", "success");
			} else {
				console.error("Attempting to change status to Pending Internal Review for a Claim that doesn't exist!");
				toast("Error", "Unable to change status to Pending Internal Review, please contact Support", "error");
			}
		} catch (err) {
			console.error("There was a problem changing status to Pending Internal Review\n", err);
			toast("Error", "There was a problem changing status to Pending Internal Review", "error");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUpsertChecklistItem = async (
		id: number | undefined,
		label: string,
		checklistType: ChecklistType,
		value: boolean
	) => {
		try {
			if (claim && claim.claimId) {
				await upsertChecklistItem(claim.claimId, id, label, checklistType, value);
				await _handleFetchClaim(claim.claimId);
			}
		} catch (error) {
			handleError(toast, error as RpcError, "Failed to update claim checklist", "Error updating claim checklist");
		}
	};

	const handleAssignAnalyst = async (analystId: number) => {
		try {
			if (claim && claim.claimId) {
				const response = await assignAnalyst(claim.claimId, analystId);
				const assignedAnalyst = response.getAssignedAnalyst();
				toast("Success", "Successfully assigned analyst " + assignedAnalyst?.getName(), "success");
				await _handleFetchClaim(claim.claimId);
			}
		} catch (error) {
			handleError(toast, error as RpcError, "Failed to assign analyst", "Error assigning analyst");
		}
	};

	const handleLoadMoreNotes = useCallback(
		async (filterRole?: string, filterNoteType?: string) => {
			if (!hasMoreNotes || !claim?.claimId) return;

			try {
				const response = await getNotesByClaim(
					claim.claimId,
					currentNotePage,
					NOTES_PER_PAGE,
					filterRole,
					filterNoteType
				);
				const notesList = response.getNotesList();
				const paginationResponse = response.getPaginationResponse();

				const notesArray = notesList
					.filter((note) => !note.getIsPinned()) // Exclude pinned notes - they're fetched separately
					.map((note) => {
						const noteType = note.getNoteType();
						return {
							id: note.getId(),
							claimId: note.getClaimId(),
							userId: note.getUserId(),
							userName: note.getUserName(),
							userRole: note.getUserRole(),
							type: {
								noteType: noteType?.getName(),
								label: noteType?.getLabel(),
								color: noteType?.getColor()
							},
							message: note.getNote(),
							isPinned: note.getIsPinned(),
							createdAt: note.getCreatedAt()
						};
					});

				if (notesArray.length > 0) {
					setNotes((prev) => [...prev, ...notesArray]);
				}

				if (paginationResponse) {
					const totalItems = paginationResponse.getTotal();
					const totalPages = Math.ceil(totalItems / NOTES_PER_PAGE);
					setHasMoreNotes(currentNotePage < totalPages);
					setCurrentNotePage((prev) => prev + 1);
				} else {
					setHasMoreNotes(notesArray.length === NOTES_PER_PAGE);
					if (notesArray.length === NOTES_PER_PAGE) {
						setCurrentNotePage((prev) => prev + 1);
					}
				}
			} catch (error) {
				console.error("Error loading more notes:", error);
				setHasMoreNotes(false);
			}
		},
		[hasMoreNotes, claim?.claimId, currentNotePage]
	);

	const handlePinNote = async (noteId: number) => {
		try {
			// Update note pin
			await pinNote(noteId);
			toast("Success", "Successfully pinned note", "success");
			// If successful, find the note locally (this might actually fail)
			const updatedNote = notes.find((item) => item.id === noteId);
			// Try to update main list of notes
			setNotes(
				notes.map((note) => {
					if (note.id === noteId) {
						return { ...note, isPinned: true };
					} else {
						return note;
					}
				})
			);
			// Inject into the pinned notes list (if note was not found, removing pin should still work)
			if (updatedNote) {
				setPinnedNotes(pinnedNotes.concat({ ...updatedNote, isPinned: true }));
			}
			return;
		} catch (error) {
			handleError(toast, error as RpcError, "Failed to pin note", "Error pinning note");
		}
	};

	const handleUnpinNote = async (noteId: number) => {
		try {
			// Update note pin
			await unpinNote(noteId);
			toast("Success", "Successfully unpinned note", "success");
			// Try to update main list of notes
			setNotes(
				notes.map((note) => {
					if (note.id === noteId) {
						return { ...note, isPinned: false };
					} else {
						return note;
					}
				})
			);
			// Remove from the pinned notes list (if note was not found, removing pin should still work)
			setPinnedNotes(pinnedNotes.filter((note) => note.id !== noteId));
			return;
		} catch (error) {
			handleError(toast, error as RpcError, "Failed to unpin note", "Error unpinning note");
		}
	};

	const handleIsPremiumPaid = async (): Promise<void> => {
		try {
			if (claim && claim.claimId) {
				const response = await getIsPremiumPaid(claim.claimId);
				setIsPremiumPaid(response.getIsPremiumPaid());
			}
		} catch (err) {
			handleError(
				toast,
				err as RpcError,
				"Unable to determine if Premium is Paid",
				"Unable to determine if Premium is Paid"
			);
		}
	};

	const handleUpdateClaimDueDate = async (newDate: Date): Promise<void> => {
		setIsLoading(true);
		try {
			if (claim && claim.claimId) {
				const postRequest: Array<ClaimsDueDates> = [];
				const singleUpdate: ClaimsDueDates = new ClaimsDueDates();
				singleUpdate.setClaimId(claim.claimId);
				singleUpdate.setDueDate(dateToInt64UTC(newDate));
				postRequest.push(singleUpdate);
				await postUpdateClaimsDueDates(postRequest);
			}
		} catch (err) {
			handleError(toast, err as RpcError, "Unable to update Due Date", "Unable to update Due Date");
		} finally {
			setIsLoading(false);
		}
	};

	const handleUpdateClaimCleanDate = async (newDate: Date): Promise<void> => {
		setIsLoading(true);
		try {
			if (claim && claim.claimId) {
				await postUpdateClaimCleanDate(claim.claimId, dateToInt64UTC(newDate));
			}
		} catch (err) {
			handleError(toast, err as RpcError, "Unable to update Clean Date", "Unable to update Clean Date");
		} finally {
			setIsLoading(false);
		}
	};

	const handleGeneratePdf = async (openPdfInNewTab = true): Promise<void> => {
		setIsLoading(true);
		try {
			if (claim && claim.claimId) {
				const response = await generateCcsPdf(claim.claimId);
				const pdfUrl = response.getUrl();
				if (pdfUrl) {
					if (openPdfInNewTab) {
						window.open(pdfUrl, "_blank");
						toast("Success", "PDF generated successfully", "success");
					}
				} else {
					toast("Error", "No PDF URL returned", "error");
				}
			} else {
				toast("Error", "No claim selected", "error");
			}
		} catch (err) {
			handleError(toast, err as RpcError, "Unable to generate PDF", "Error generating PDF");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (hasAdminAccess && claim?.claimId) {
			setNotes([]);
			setCurrentNotePage(1);
			setHasMoreNotes(true);
			handleFetchNotes(claim.claimId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [claim?.claimId]);

	return (
		<ClaimDetailsContext.Provider
			value={{
				claim,
				claimCase,
				benefits,
				analysts,
				activities,
				notes,
				pinnedNotes,
				activityTypes,
				isLoading,
				hasMoreActivities,
				hasMoreNotes,
				isPremiumPaid,
				handleFetchClaim,
				handleApproveClaim,
				handleDenyClaim,
				handleInReview,
				handleCancelClaim,
				handleUncancelClaim,
				handleQueueClaimForPayment,
				handleClosePaidClaim,
				handleCloseNotPaidClaim,
				handleCloseNotPaidGoodwillClaim,
				handleDuplicateClaim,
				handlePendingProvider,
				handlePendingVerification,
				handlePendingInternalReview,
				handleClaimUpsert,
				handleHoldForAuditChange,
				handleFetchActivities,
				handleLoadMoreActivities,
				handleAssignAnalyst,
				handleUpsertChecklistItem,
				handleFetchNotes,
				handleFetchPinnedNotes,
				handleCreateNote,
				handleLoadMoreNotes,
				handlePinNote,
				handleUnpinNote,
				getServiceLabel,
				handleIsPremiumPaid,
				handleUpdateClaimDueDate,
				handleUpdateClaimCleanDate,
				handleGeneratePdf
			}}>
			{children}
		</ClaimDetailsContext.Provider>
	);
};

export const useClaimDetails = (analystFilter?: string) => {
	const context = useContext(ClaimDetailsContext);
	if (!context) throw new Error("useClaimDetails must be used within a ClaimProvider");
	return context;
};
